<template>
  <div>
    <div v-if="!is_logged" class="m3p-contact__login">
      <a
        class="m3-button m3-button--primary"
        :href="`https://www.m3.com/login?${redirectUrlFromM3Login}`"
        >m3.com会員の方は会員情報を自動入力（ログイン）</a
      >
    </div>
    <table class="m3p-contact__table">
      <name-form-group
        :firstName="formData.first_name"
        :lastName="formData.last_name"
        :isLogged="is_logged"
        @change="(name, value) => updateFormDataValue(name, value)"
      />

      <name-kana-form-group
        :firstNameKana="formData.first_name_kana"
        :lastNameKana="formData.last_name_kana"
        :isLogged="is_logged"
        :editable="true"
        @change="(name, value) => updateFormDataValue(name, value)"
      />

      <email-form />

      <prefecture-and-address-form-group
        :prefectureMaster="master_data.prefectures"
      />

      <phone-form />

      <tr>
        <sex-form />
        <age-form :ageMaster="master_data.birth_years" />
      </tr>
      <tr>
        <wishful-time-form :wishfulTimeMaster="master_data.wishful_times" />
        <work-status-form />
      </tr>

      <experience-form-group
        :positionTypeMaster="master_data.position_types"
        :experienceYearMaster="master_data.experience_years"
        :experiencesAttributes="formData.experiences_attributes"
        @change="(name, value) => updateFormDataValue(name, value)"
      />

      <message-form
        :message="formData.message"
        :formOpened="messageFormOpened"
        @change="(name, value) => updateFormDataValue(name, value)"
        @on-open-modal="() => changeModalName(`messageExamples`)"
      />
    </table>

    <div v-if="!messageFormOpened" class="m3p-contact__table__add">
      <button @click="openMessageForm" class="m3p-smalltext__message">
        <i class="atlas-glyphs-m-arrow-down"></i>メッセージを記載する
      </button>
    </div>
    <div class="m3p-contact__submit">
      <div
        v-show="should_recaptcha"
        class="g-recaptcha"
        :data-sitekey="site_key"
      ></div>
      <p class="m3p-contact__submit__cont">
        <a href="/help/content#06" target="_blank"
          >利用規約<i class="atlas-glyphs-external-link"></i></a
        >・
        <a href="https://www.m3career.com/privacy" target="_blank"
          >個人情報保護方針<i class="atlas-glyphs-external-link"></i
        ></a>
        についてご同意の上、登録フォーム情報をご送信ください
      </p>
      <a
        href="#"
        id="detail_contact_confirm"
        class="m3-button m3-button--secondary m3p-button__submit"
        @click.prevent="submitFormData"
      >
        この内容で送信する
        <i class="atlas-glyphs-m-arrow-right"></i
      ></a>
    </div>
    <modal
      v-if="currentModalName === `m3MemeberLoginForm`"
      header="ログイン"
      :opened="modalOpened"
      @close="() => changeModalName('')"
    >
    </modal>
    <modal
      v-if="currentModalName === `messageExamples`"
      header="お問い合わせ用文例"
      :opened="modalOpened"
      @close="() => changeModalName('')"
    >
      <message-examples-content :company="company" />
    </modal>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { useStore } from 'vuex';
import qs from 'qs';
import sendPositionInquiryMail from '@component_path/lib/call_api/inquiry_mail/positionInquiryMail';
import Cookies from 'js-cookie';
import pcrContents from '@component_path/pages/pc/positions/show/module/pcr_contents';
import NameFormGroup from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/NameGroup';
import NameKanaFormGroup from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/NameKanaGroup';
import EmailForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/Email';
import PrefectureAndAddressFormGroup from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/PrefectureAndAddressGroup';
import PhoneForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/Phone';
import SexForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/Sex';
import AgeForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/Age';
import WishfulTimeForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/WishfulTime';
import WorkStatusForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/WorkStatus';
import ExperienceFormGroup from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/ExperienceGroup';
import MessageForm from '@component_path/pages/pc/positions/show/inquiry_form/components/inquiry_form_item/Message';

import Modal from '@component_path/pages/pc/positions/show/common_components/modal/Modal';
import MessageExamplesContent from '@component_path/pages/pc/positions/show/inquiry_form/components/modal_content/MessageExamplesContent';

const store = useStore();
const { validate } = useForm();

const props = defineProps({
  master_data: {
    type: Object,
    required: true
  },
  form_options: {
    type: Object,
    required: true
  },
  initial_data: {
    type: Object,
    required: true
  },
  is_logged: {
    type: Boolean,
    required: true
  },
  should_recaptcha: {
    type: Boolean,
    required: false,
    default: true
  },
  site_key: {
    type: String,
    required: true
  },
  company: {
    type: Object,
    required: true
  }
});

const messageFormOpened = ref(false);
const currentModalName = ref('');
const grecaptchaResponse = ref(null);

const pcrParams = computed(() => store.state.requestParam.pcrParams);
const formData = computed(() => store.state.positionInquiryMail.formData);
const isProcessing = computed(() => store.state.requestParam.isProcessing);
const currentAuthenticityToken = computed(
  () => store.state.requestParam.currentAuthenticityToken
);
const submitFormUrlWithQuery = computed(
  () => store.getters['requestParam/submitFormUrlWithQuery']
);
const modalOpened = computed(() => currentModalName.value !== '');
const redirectUrlFromM3Login = computed(() =>
  qs.stringify({ origURL: window.location.href })
);

const setFormItem = formItem =>
  store.dispatch('positionInquiryMail/setFormItem', formItem);
const initFormData = formData =>
  store.dispatch('positionInquiryMail/initFormData', formData);
const setIsProcessing = isProcessing =>
  store.dispatch('requestParam/setIsProcessing', isProcessing);
const setSubmitFormUrl = url =>
  store.dispatch('requestParam/setSubmitFormUrl', url);
const setPcrParams = params =>
  store.dispatch('requestParam/setPcrParams', params);
const setCurrentAuthenticityToken = token =>
  store.dispatch('requestParam/setCurrentAuthenticityToken', token);

const updateFormDataValue = (field, value) => {
  setFormItem({ field, value });
};
const submitFormData = async () => {
  // 二重送信防止用のフラグが立っていたら、送信しない。
  const { valid } = await validate();
  if (isProcessing.value || !valid) return;
  setIsProcessing(true);

  Object.keys(pcrParams.value).map(key => {
    Cookies.set(key, pcrParams.value[key], { domain: '.m3.com' });
  });

  // recaptchaを読み込んでないページでgrecaptcha.getResponse()がエラーを吐くため
  try {
    if (typeof grecaptcha === 'object') {
      grecaptchaResponse.value = grecaptcha.getResponse();
    }
  } catch (e) {
    // recaptchaを読み込んでないページでgrecaptcha.getResponse()がエラーを吐いて問い合わせ処理が
    // 続行できなくなるため例外処理を書いて問い合わせを続行させる
    // TODO: No reCAPTCHA client existsというエラーを解消する
    grecaptchaResponse.value = null;
  }

  const response = await sendPositionInquiryMail(
    formData.value,
    currentAuthenticityToken.value,
    submitFormUrlWithQuery.value,
    grecaptchaResponse.value
  );
  if (response.status === 200) {
    const redirectUrl = response.data.thanks_page_url;
    location.href = redirectUrl;
    return;
  } else {
    setCurrentAuthenticityToken(response.data.authenticity_token);
  }
  setIsProcessing(false);
};
const openMessageForm = () => (messageFormOpened.value = true);
const changeModalName = name => (currentModalName.value = name);

setPcrParams({ key: 'pcr_content', value: pcrContents.direct });
setSubmitFormUrl(props.form_options.form_url);
setCurrentAuthenticityToken(props.form_options.authenticity_token);
initFormData(props.initial_data);
</script>

<style>
.m3p-form__strong input {
  border: 1px solid #ec7722 !important;
}

.m3-textbox input {
  padding: 6px 8px 8px;
  border: 1px solid #9ac;
  border-radius: 4px;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.m3-textbox {
  padding: none;
  border: none !important;
  border-radius: none;
  -webkit-box-sizing: none;
  -moz-box-sizing: none;
  box-sizing: none;
}

.m3p-textbox input {
  border: 1px solid #9ac;
  font-size: 16px;
  padding: 10px;
  border-radius: 4px;
  vertical-align: middle;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.m3p-textbox input:focus {
  background-color: #fdf8dc;
}

.m3p-form__errorbox input,
.m3p-form__errorbox select {
  border: 1px solid #c00;
  background-color: #ffe0e0;
}

.m3p-form-radio li {
  display: inline-block;
}
</style>
